import { z } from 'zod';
import { mapLayerKeySchema } from './mapping';

/**
 * Importing a project's geography.
 *
 * The same problem the schedule import solves, one dimension over: the
 * alignment, the sites, the work areas and the constraints arrive from a
 * surveyor or a GIS team as a GeoJSON file, and drawing four hundred of them by
 * hand is not something anybody will do.
 *
 * Same shape as the other two imports: text in a JSON body, a dry run that
 * reports what would happen, and a commit that is one transaction.
 *
 * ## One layer for the whole file
 *
 * Not a per-feature property. A GeoJSON export is almost always one kind of
 * thing -- a file of level crossings, a file of work areas -- because that is
 * how GIS layers are organised in the first place. Reading the layer from a
 * property would mean asking which property, then mapping its values onto this
 * product's five, which is a configuration screen to save a second import.
 *
 * ## The name is a property the reader picks, and the dry run says which exist
 *
 * There is no convention worth guessing here: the field is `name` in one export
 * and `NIMI`, `TRACK_ID` or `objectid` in the next. So a dry run reports the
 * properties the file actually carries, and the reader chooses -- which turns
 * "your file is wrong" into "which of these is the name".
 *
 * When `nameProperty` is absent the import tries the handful of spellings that
 * genuinely are conventional, and reports the rest as a choice to make.
 */

/** Tried in order when the caller names no property. Nothing else is guessed. */
export const CONVENTIONAL_NAME_PROPERTIES = ['name', 'Name', 'NAME', 'title', 'nimi'] as const;

export const mapImportSchema = z
  .object({
    /**
     * The file, as text. Five million characters is a large alignment; past
     * that a reader is importing a basemap, which is a different feature and
     * one this product deliberately does not have.
     */
    geojson: z.string().min(1).max(5_000_000),
    layer: mapLayerKeySchema,
    /** The property holding each feature's name. Absent means "try the
     *  conventional spellings, and tell me what the file has". */
    nameProperty: z.string().min(1).max(120).optional(),
    /** When false the response reports what would happen and writes nothing. */
    commit: z.boolean().default(false),
  })
  .strict();
export type MapImport = z.infer<typeof mapImportSchema>;

export const mapImportRowErrorSchema = z.object({
  /** The feature's position in the collection, 1-based, since GeoJSON has no
   *  line numbers a reader could look up. */
  row: z.number().int().positive(),
  code: z.string(),
  params: z.record(z.string(), z.unknown()).optional(),
});
export type MapImportRowError = z.infer<typeof mapImportRowErrorSchema>;

export const mapImportResultSchema = z.object({
  committed: z.boolean(),
  featuresRead: z.number().int().nonnegative(),
  featuresAccepted: z.number().int().nonnegative(),
  featuresRejected: z.number().int().nonnegative(),
  /** By drawing kind, which is what a reader recognises: "three hundred points
   *  and one line" is a file they can identify. */
  pointCount: z.number().int().nonnegative(),
  lineCount: z.number().int().nonnegative(),
  areaCount: z.number().int().nonnegative(),
  /**
   * Every property name the file carries, so the reader can pick which one is
   * the name instead of being told their file is wrong. Bounded, because a
   * malformed file can carry a property per feature.
   */
  availableProperties: z.array(z.string()).max(100),
  /** The property that was actually used, or null when none resolved. */
  nameProperty: z.string().nullable(),
  errors: z.array(mapImportRowErrorSchema).max(200),
});
export type MapImportResult = z.infer<typeof mapImportResultSchema>;
